import { reactive, shallowRef, toRef } from 'vue';

import axios from 'axios';

import { useTransactionsAPIService } from '@/composables/services/useTransactionsAPIService';
import { type ClientEECode, useErrorModal } from '@/composables/useErrorModal';
import { createPromiseWithAbortSignal } from '@/helpers/promises';
import { captureSentryException } from '@/logs/sentry';
import {
  type StatementDownloadResult,
  type Transaction,
  TransactionType
} from '@/references/axios/transactions/types';
import { MINUTE } from '@/references/constants';
import { ExpectedError } from '@/references/ExpectedError';
import { HHError } from '@/references/HHError';
import { t } from '@/references/i18n';

const transactions = shallowRef<Transaction[]>([]);
const state = reactive<{
  loading: boolean;
  loaded: boolean;
}>({
  loading: false,
  loaded: false
});

let updateTransactionsAbortController = new AbortController();

export async function getStatements(id: string, type: TransactionType): Promise<void> {
  const service = useTransactionsAPIService();
  const { notify, auto } = useErrorModal();

  async function wrapDownloadAction(fn: () => Promise<StatementDownloadResult>): Promise<void> {
    try {
      notify(t('toasts.downloadStarted'));
      const { filename, blob } = await fn();
      const objectURL = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectURL;
      link.setAttribute('download', filename);
      link.click();
      setTimeout(() => window.URL.revokeObjectURL(objectURL), 0);
      notify(t('toasts.downloadFinished'));
    } catch (error) {
      auto(
        new ExpectedError<ClientEECode>('downloadReceipt', { cause: error, sentryHandle: true })
      );
    }
  }

  switch (type) {
    case TransactionType.OTC:
      return wrapDownloadAction(() => service.getOTCStatement(id));
  }
}

async function load(): Promise<void> {
  try {
    const service = useTransactionsAPIService();

    updateTransactionsAbortController.abort();
    updateTransactionsAbortController = new AbortController();

    state.loading = true;
    transactions.value = await service.getTransactions({
      signal: updateTransactionsAbortController.signal
    });
    state.loading = false;
    state.loaded = true;
  } catch (error) {
    if (!axios.isCancel(error)) {
      state.loading = false;
      captureSentryException(new Error('Failed to get transactions', { cause: error }));
    }
  }
}

function waitForTransaction(
  id: string,
  timeoutTime = 10 * MINUTE,
  abort: AbortSignal
): Promise<void> {
  const { resolve, reject, wait, onSignal } = createPromiseWithAbortSignal<void, HHError>(abort);

  const timeout = setTimeout(() => {
    reject(new HHError('Transaction failed (by timeout)', { payload: { timeout: timeoutTime } }));
    clearInterval(interval);
  }, timeoutTime);

  let loading: boolean = false;
  const interval = setInterval(async () => {
    if (loading) {
      return;
    }
    loading = true;
    await load();
    const tx = transactions.value.find((item) => {
      return item.id === id;
    });
    loading = false;
    if (tx === null || tx === undefined) {
      return;
    }

    resolve();
    clearTimeout(timeout);
    clearInterval(interval);
  }, 500);

  onSignal(() => {
    clearTimeout(timeout);
    clearInterval(interval);
  });

  return wait();
}

export function useTransactions() {
  return {
    loading: toRef(state, 'loading'),
    loaded: toRef(state, 'loaded'),
    transactions,
    getStatements,
    load,
    waitForTransaction
  };
}
